import { noop } from '@tanstack/react-query'

import { prefetchRouteData } from '@/app/query-client'
import {
  chartConfigQueryOptions,
  chartHistoryHeadQueryOptions,
  chartSymbolQueryOptions,
} from '@/features/trading-chart/api/queries'
import { readChartPriceBasis, rememberLastChartSymbol } from '@/features/trading-chart/lib/lastUsed'
import {
  readWarmConfig,
  readWarmSymbol,
  startWarmRead,
} from '@/features/trading-chart/lib/warmCache'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/chart/$symbol` loader. It starts the symbol, config and first bars
 * reads and returns at once: the page renders the chart beside them, and the
 * chart's `onReady`, `resolveSymbol` and first `getBars` ask the same
 * queries, so they wait on these requests instead of making their own while
 * the library starts. Awaiting the symbol here held the chart, and the
 * library behind it, back by a whole round trip.
 *
 * A repeat visit answers the symbol and config from the warm cache
 * (lib/warmCache.ts) while the requests go out to replace them.
 *
 * Its own module, away from the screen, for the reason docs/route-loading.md
 * gives: a loader is not code-split, and it must not pull a component in.
 *
 * An unknown symbol resolves to `null`, which the screen swaps the chart for
 * its not-found branch on (docs/route-loading.md, "Not-found answers"). It is
 * not thrown as the router's `notFound()`: that import alone moves every route
 * module into the entry chunk (measured: +83 kB), and no other loader uses it.
 *
 * A preload (hover) reads but does not remember: only a chart the member
 * actually opened becomes the one `/chart` returns to. A symbol switched inside
 * the chart re-runs this loader and answers from the cache.
 */
export function loadChartScreen({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  preload: boolean
}): void {
  const symbol = params.symbol.toUpperCase()
  // A failure of either is the screen's to show (the config's as the
  // datafeed's fallback, the symbol's as the route error page), through the
  // same query entry; these promises only start the reads.
  startWarmRead(queryClient, chartConfigQueryOptions(), readWarmConfig()).catch(noop)
  startWarmRead(queryClient, chartSymbolQueryOptions(symbol), readWarmSymbol(symbol)).then(
    (info) => {
      if (info !== null && !preload) rememberLastChartSymbol(symbol)
    },
    noop,
  )
  // Not for a hover: bars are kept a minute, and the chart opens in a new tab
  // that cannot read this one's cache.
  if (!preload)
    prefetchRouteData(queryClient, chartHistoryHeadQueryOptions(symbol, readChartPriceBasis()))
}
